import cn from 'classnames';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import openPopupOperation from 'editor/src/store/app/module/popup/operation/openPopupOperation';
import addTextInStyleOperation from 'editor/src/store/design/operation/addTextInStyleOperation';
import assignTextStyleToSelectionOperation from 'editor/src/store/design/operation/assignTextStyleToSelectionOperation';
import createTextStyleOperation from 'editor/src/store/design/operation/createTextStyleOperation';
import removeTextStyleOperation from 'editor/src/store/design/operation/removeTextStyleOperation';
import updateTextStyleOperation, {
  TextStyleUpdateResult,
} from 'editor/src/store/design/operation/updateTextStyleOperation';
import getSelectedMediaElementsObjects from 'editor/src/store/design/selector/getSelectedMediaElementsObjects';
import getSelectionTextStyle from 'editor/src/store/design/selector/getSelectionTextStyle';
import { MediaText, TextStyle } from 'editor/src/store/design/types';
import { getTextStyleValuesFromElement, TextStyleValues } from 'editor/src/store/design/util/textStyles';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import isAddElementsAllowed from 'editor/src/store/editor/selector/isAddElementsAllowed';
import isTextStylesAllowed from 'editor/src/store/editor/selector/isTextStylesAllowed';
import getNewTextFontFamily from 'editor/src/store/fonts/selector/getNewTextFontFamily';
import { useDispatch, useSelector, useStore } from 'editor/src/store/hooks';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import toastController from 'editor/src/util/toastController';

import IconInfo from 'editor/src/component/Icon/IconInfo';
import IconPlus from 'editor/src/component/Icon/IconPlus';
import { PopupName } from 'editor/src/component/Popup/popups';
import { FONT_FAILED_TOAST_KEY } from 'editor/src/component/TextStyleControl/constants';
import useLoadTextStyleFonts from 'editor/src/component/TextStyleControl/useLoadTextStyleFonts';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';
import WithTooltip from 'editor/src/component/WithTooltip';

import TextStyleCard, { TextStyleCardAction } from './TextStyleCard';
import useTextStyleUsage from './useTextStyleUsage';

import styles from './index.module.scss';

const NO_STYLES: TextStyle[] = [];

const getFirstSelectedText = (state: Parameters<typeof getSelectedMediaElementsObjects>[0]) =>
  getSelectedMediaElementsObjects(state).find((element): element is MediaText => element?.type === 'text');

/**
 * GAPI-61895: the design's text styles as preview cards in the Text tab.
 *
 * - Click a card: with texts selected it applies the style to them; with no text selected it
 *   adds a new text in that style on the current page.
 * - Authors: "+ New style" saves the selected text as a style ("Style N"), or with nothing
 *   selected creates one from the default text look, and opens it in the style editor with its
 *   name ready to type over; each card has a "⋯" menu with Edit style (the style editor, which
 *   replaces the tab's content: see TextStyleEditorView), Rename, Duplicate and Delete (confirmed).
 * - Buyers (embedded editor) only get the cards, and only when the design has styles.
 *
 * Gated on isTextStylesAllowed (host setting; in the embedded editor also the template's
 * "Add layers" switch). Outside the embedded editor a design that already has styles shows
 * them, so an imported template's styles stay manageable for its author.
 */
interface Props {
  /** Opens the style editor in place of the tab's content; `focusName` for a style just created. */
  onEdit: (styleId: string, focusName: boolean) => void;
}

function TextStylesSection({ onEdit }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();
  const isMobile = useIsMobile();
  const enabled = useSelector(isTextStylesAllowed);
  const textStyles = useSelector((state) => state.design.designData?.textStyles ?? NO_STYLES, shallowEqual);
  const selection = useSelector(getSelectionTextStyle, shallowEqual);
  const firstSelectedText = useSelector(getFirstSelectedText);
  const addElementsAllowed = useSelector(isAddElementsAllowed);
  const usage = useTextStyleUsage();
  useLoadTextStyleFonts(textStyles);

  const canManage = !isEmbedded;
  const defaultName = t('editor-text-styles-default-name', { number: textStyles.length + 1 });

  const warnIfFailed = (result: TextStyleUpdateResult) => {
    if (result === 'failed') {
      toastController.warning(t(FONT_FAILED_TOAST_KEY), '');
    }
  };

  const onApply = useCallback(
    async (style: TextStyle) => {
      const state = store.getState();
      if (getFirstSelectedText(state)) {
        warnIfFailed(await dispatch(assignTextStyleToSelectionOperation(style.id)));
      } else if (isAddElementsAllowed(state)) {
        warnIfFailed(await dispatch(addTextInStyleOperation(style.id, getCurrentSpreadIndex(state))));
      }
    },
    [store],
  );

  const onRename = useCallback((style: TextStyle, name: string) => {
    void dispatch(updateTextStyleOperation(style.id, { name }));
  }, []);

  const onAction = useCallback(
    (style: TextStyle, action: TextStyleCardAction) => {
      const { id, name, ...values } = style;
      switch (action) {
        case 'edit':
          onEdit(id, false);
          break;
        case 'duplicate':
          dispatch(createTextStyleOperation(t('editor-text-style-copy-name', { name }), values, false));
          break;
        case 'delete': {
          const count = usage[id]?.usage ?? 0;
          void dispatch(
            openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
              popupTitle: t('editor-text-style-delete-title', { name }),
              textLines: [
                count === 1 ? t('editor-text-style-delete-text-one') : t('editor-text-style-delete-text', { count }),
              ],
              options: [
                { title: t('editor-cancel') },
                { title: t('editor-text-style-remove'), onConfirm: () => dispatch(removeTextStyleOperation(id)) },
              ],
              hideTitleIcon: true,
              className: 'cy-text-style-delete-confirm',
            }),
          );
          break;
        }
        default:
      }
    },
    [usage, onEdit],
  );

  if (!enabled && (isEmbedded || !textStyles.length)) {
    return null;
  }
  // Buyers only pick from the template's styles.
  if (!canManage && !textStyles.length) {
    return null;
  }

  const onNewStyle = () => {
    const state = store.getState();
    const text = getFirstSelectedText(state);
    let id: string;
    if (text) {
      // Saves the selected text's look and makes it follow the new style.
      id = dispatch(createTextStyleOperation(defaultName, getTextStyleValuesFromElement(text), true));
    } else {
      // Nothing selected: start from the look a new text gets.
      const defaults = state.fonts.defaultFontStyles;
      const values: TextStyleValues = {
        fontFamily: getNewTextFontFamily(state),
        fontSize: defaults.fontSize,
        color: defaults.color,
        lineHeight: defaults.lineHeight,
      };
      id = dispatch(createTextStyleOperation(defaultName, values, false));
    }
    onEdit(id, true);
  };

  const activeId = selection.kind === 'style' ? selection.style.id : undefined;
  const addsText = !firstSelectedText && addElementsAllowed;

  return (
    <div className={cn(styles.TextStylesSection, 'cy-text-styles-section')}>
      <div className={styles.header}>
        <div className={styles.title}>{t('editor-text-styles-title')}</div>
        <WithTooltip overlay={t('editor-text-styles-info')} className={styles.infoTooltip}>
          <span className={cn(styles.infoIcon, 'cy-text-styles-info')}>
            <IconInfo size="small" />
          </span>
        </WithTooltip>
        {canManage && (
          <button type="button" className={cn(styles.newStyleButton, 'cy-text-styles-new')} onClick={onNewStyle}>
            <IconPlus />
            {firstSelectedText ? t('editor-text-styles-save-selected') : t('editor-text-styles-new')}
          </button>
        )}
      </div>

      {!textStyles.length && (
        <div className={cn(styles.empty, 'cy-text-styles-empty')}>{t('editor-text-styles-empty')}</div>
      )}

      <div className={cn(styles.cards, { [styles.grid]: isMobile })}>
        {textStyles.map((style) => (
          <TextStyleCard
            key={style.id}
            style={style}
            usage={usage[style.id]?.usage ?? 0}
            modified={usage[style.id]?.modified ?? 0}
            active={style.id === activeId}
            addsText={addsText}
            canManage={canManage}
            compact={isMobile}
            onApply={onApply}
            onAction={onAction}
            onRename={onRename}
          />
        ))}
      </div>
    </div>
  );
}

export default React.memo(TextStylesSection);
